import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { api } from '@/shared/lib/axios';

const sessionSchema = z.object({
  id: z.string(),
  deviceName: z.string(),
  deviceType: z.enum(['web', 'ios', 'android', 'unknown']),
  lastUsedAt: z.string(),
  createdAt: z.string(),
  isCurrent: z.boolean(),
});

export type AuthSession = z.infer<typeof sessionSchema>;

const sessionsResponseSchema = z.object({ sessions: z.array(sessionSchema) });

export const SESSIONS_QUERY_KEY = ['auth', 'sessions'] as const;

async function fetchSessions(): Promise<AuthSession[]> {
  const res = await api.get('/api/auth/sessions');
  return sessionsResponseSchema.parse(res.data).sessions;
}

export function useSessions(enabled = true) {
  return useQuery({
    queryKey: SESSIONS_QUERY_KEY,
    queryFn: fetchSessions,
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      await api.delete(`/api/auth/sessions/${sessionId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY }),
  });
}

export function useLogoutAll() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post('/api/auth/logout-all');
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY }),
  });
}
